import * as React from 'react';
import { Logger, withLogger } from '../../../helpers/bi';
import { isNameAvailable, CAPTCHA_SITE_KEY } from '../../../helpers/api.client';
import { BIEvent } from '../../../helpers/bi/event-type';
import { SelectOption, TinyLoader } from '@wix/answers-ui-libby';
import { MarketingInput, MarketingSelect } from '@wix/answers-common-components';
import { getQueryParamByName, ANSWERS_BASE_DOMAIN } from '../../../helpers/utils';
import * as ReCAPTCHA from 'react-google-recaptcha';
import { systemSupportedLanguages } from '@wix/answers-api';
import { SignupProps, SignupPropsWithWix } from '../comp';
import { withTranslator } from '../../../helpers/shared';
import { RenderHtml } from '../../../helpers/render-html';
import { TranslateFn } from '@wix/answers-lib';

const makeKebabCase = (str: string) => str.toLowerCase().replace(/^\s+/g, '').replace(/\s+/g, '-').replace(/[<>]/g, '');

const translatePrefix = 'marketing-site.signup-form.signup';

const ReCaptcha = ReCAPTCHA.default;

export type CompleteSignupState = {
	fullname: string;
	fullnameError: string;
	organizationName: string;
	organizationNameError: string;
	phoneNumber: string;
	phoneNumberError: string;
	primaryLocale: string;
	primaryLocaleError: string;
	subdomain: string;
	subdomainError: string;
	recaptchaValue: string;
	nameAvailable: boolean;
	isSubmitting: boolean;
	validating: boolean;
	token: string;
	email: string;
};

export type CompleteSignupProps = {
	onSubmit: (params: SignupProps) => void;
	onWixSubmit: (params: SignupPropsWithWix) => void;
	isWix?: boolean;
} & withLogger & withTranslator;

export class CompleteSignup extends React.PureComponent<CompleteSignupProps, CompleteSignupState> {
	logger: Logger = this.props.logger;
	compTranslate: TranslateFn = (key, params) => this.props.t(`${translatePrefix}.${key}`, params);
	requiredError = this.compTranslate('errors.required-field');

	captcha: any = null;
	subdomainCheckTimer: any = null;
	subdomainCheckDelay = 500;

	state: CompleteSignupState = {
		fullname: '',
		fullnameError: '',
		organizationName: '',
		organizationNameError: '',
		phoneNumber: '',
		phoneNumberError: '',
		primaryLocale: '',
		primaryLocaleError: '',
		subdomain: '',
		subdomainError: '',
		recaptchaValue: '',
		nameAvailable: true,
		isSubmitting: false,
		validating: false,
		token: '',
		email: ''
	};

	componentDidMount () {
		if (this.props.isWix) {
			const token = getQueryParamByName('token');
			const email = decodeURIComponent(getQueryParamByName('email') || '');
			const fullname = (getQueryParamByName('fullName') || '').replace(/\+/, ' ');

			history.replaceState({}, '', location.pathname);

			if (!token || !email) {
				location.replace('/signup');
			} else {
				this.setState({fullname, email, token});
			}
		}
	}

	componentWillUnmount () {
		clearTimeout(this.subdomainCheckTimer);
	}

	captchaRef = (ref: any) => this.captcha = ref;

	validateOrganizationName = () => {
		const {compTranslate} = this;
		const name = this.state.organizationName;
		const result = !name.trim() ? compTranslate('errors.organization-required') : '';

		if (!result && !this.state.subdomain) {
			this.onSubdomainChange(name);
		}

		this.setState({organizationNameError: result});
		return result;
	}

	validatePrimaryLocale = () => {
		const {compTranslate} = this;
		const locale = this.state.primaryLocale;
		const result = !locale ? compTranslate('errors.primary-locale') : '';

		this.setState({primaryLocaleError: result});
		return result;
	}

	validateSubdomain = () => {
		const {compTranslate} = this;
		const sub = this.state.subdomain;
		const pattern = /^[A-Za-z0-9](?:[A-Za-z0-9\-]{0,61}[A-Za-z0-9])?$/;
		let result = '';

		if (!sub) {
			result = compTranslate('errors.subdomain-required');
		} else if (sub.length < 4) {
			result = compTranslate('errors.subdomain-min-length');
		} else if (!pattern.test(sub)) {
			result = compTranslate('errors.subdomain-invalid');
		} else if (!this.state.nameAvailable) {
			result = compTranslate('errors.subdomain-taken');
		}

		this.setState({subdomainError: result});
		return result;
	}

	validateName = () => {
		const {compTranslate} = this;
		const fullName = this.state.fullname.trim();
		let result = '';

		if (!fullName) {
			result = compTranslate('errors.name-required');
		} else if (fullName.length < 3) {
			result = compTranslate('errors.name-min-length');
		}

		this.setState({fullnameError: result});
		return result;
	}

	parsePossiblePhoneNumber = (phoneNumber: string) => {
		return phoneNumber.trim().length ? {
			phoneNumber: {
				countryCode: '',
				number: phoneNumber.trim()
			}
		} : {};
	}

	getInvalidFields = () => {
		return [
			this.props.isWix && this.validateName() ? 'fullname' : '',
			this.validateOrganizationName() ? 'organization-name' : '',
			this.validatePrimaryLocale() ? 'locale' : '',
			this.validateSubdomain() ? 'subdomain' : ''
		]
		.filter((i) => !!i)
		.join(', ');
	}

	isValid = () => {
		const organizationNameError = this.validateOrganizationName();
		const primaryLocaleError = this.validatePrimaryLocale();
		const subdomainError = this.validateSubdomain();
		const maybeFullnameError  = this.props.isWix ? this.validateName() : false;

		return !organizationNameError && !primaryLocaleError && !subdomainError && !maybeFullnameError;
	}

	isNotEmpty = () => {
		const {state} = this;
		const maybeFullname = this.props.isWix ? !!state.fullname : true;
		return !!state.organizationName && !!state.primaryLocale && !!state.subdomain && maybeFullname;
	}

	safelyExecuteCaptcha = (e: any) => {
		e.stopPropagation();
		e.preventDefault();

		const {state} = this;

		if (state.validating || state.isSubmitting) {
			return;
		}

		const params = {
			organizationName: state.organizationName,
			locale: state.primaryLocale,
			subdomain: state.subdomain,
			...this.parsePossiblePhoneNumber(state.phoneNumber)
		};

		this.logger.logEvent(BIEvent.SIGNUP_CLICK_STEP2, {subdomain: params.subdomain});

		if (!!this.captcha && this.isValid()) {
			this.setState({isSubmitting: true});

			if (!(window as any).__isProduction) {
				this.onCaptchaResolve('');
			} else {
				this.captcha.execute();
			}
		} else {
			const invalidFields = this.getInvalidFields();
			this.logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, {...params, error: `Form validation failed in fields - [${invalidFields}]`});
		}
	}

	organizationNameOnChange = (val: string) => this.setState({organizationName: val, organizationNameError: ''});

	onNameChange = (val: string) => this.setState({fullname: val, fullnameError: ''});

	onLocaleChange = (option: SelectOption) => this.setState({primaryLocale: option.value, primaryLocaleError: ''}, this.validatePrimaryLocale);

	onPhoneNumberChange = (val: string) => this.setState({phoneNumber: val, phoneNumberError: ''});

	onSubdomainChange = (val: string) => {
		const processedValue = makeKebabCase(val);
		this.setState({subdomain: processedValue, subdomainError: ''}, () => {
			clearTimeout(this.subdomainCheckTimer);

			this.setState({nameAvailable: true});

			const error = this.validateSubdomain();
			if (!error) {
				this.subdomainCheckTimer = setTimeout(() => {
					this.setState({validating: true});
					isNameAvailable(processedValue).then((res) => {
						if (!res) {
							this.logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, {subdomain: val, error: 'Subdomain taken'});
						}

						this.setState({nameAvailable: res, validating: false}, () => {
							this.setState({subdomainError: this.validateSubdomain()});
						});
					}, () => this.setState({validating: false}));
				}, this.subdomainCheckDelay);
			}
		});
	}

	onCaptchaResolve = (recaptchaValue: string) => {
		const {state} = this;

		const params = {
			subdomain: state.subdomain,
			locale: getQueryParamByName('overrideLocale') || state.primaryLocale,
			organizationName: state.organizationName,
			recaptchaValue,
			...this.parsePossiblePhoneNumber(state.phoneNumber)
		};

		if (this.props.isWix) {
			this.props.onWixSubmit({...params, token: state.token, userName: state.fullname, email: state.email});
		} else {
			this.props.onSubmit(params);
		}
	}

	signupEnabledLocales() {
		// remove after https://app.asana.com/0/1201446965042756/1201912981754421

		return systemSupportedLanguages.filter(lang => lang !== 'zh');
	}

	localesOptions = (): SelectOption[] => this.signupEnabledLocales().map((lang) => {
		const isSelectedClass = this.state.primaryLocale === lang ? ' is-selected' : '';

		return {label: this.props.t(`common.lang.${lang}`), value: lang, className: `option-${lang}${isSelectedClass}`};
	})

	render () {
		const {state, props, compTranslate} = this;

		const currentLocale = state.primaryLocale ? {label: props.t(`common.lang.${state.primaryLocale}`), value: state.primaryLocale} : null;
		const localeOptions = this.localesOptions();

		const maybeWixSignup = !props.isWix ? null :
			<MarketingInput className='wix-fullname' error={state.fullnameError} value={state.fullname} type='text' label={compTranslate('fields.name')} onChange={this.onNameChange} onBlur={this.validateName} />;

		const formComp = (
			<form className='complete-signup-form' onSubmit={this.safelyExecuteCaptcha}>
				{maybeWixSignup}
				<MarketingInput className='organization' error={state.organizationNameError} value={state.organizationName} type='text' label={compTranslate('fields.organization-name')} onChange={this.organizationNameOnChange}  onBlur={this.validateOrganizationName} startFocused={true}/>
				<MarketingInput className='subdomain' maxLength={30} fixedPlaceholder={ANSWERS_BASE_DOMAIN} error={state.subdomainError} value={state.subdomain} type='text' label={compTranslate('fields.subdomain')} onChange={this.onSubdomainChange} infoText={compTranslate('tooltip.subdomain')} onBlur={this.validateSubdomain}/>
				<MarketingSelect className='locale' error={state.primaryLocaleError} value={currentLocale} options={localeOptions} label={compTranslate('fields.primary-locale')} onChange={this.onLocaleChange} />
				<MarketingInput className='phone' maxLength={20} error={''} value={state.phoneNumber} type='text' label={compTranslate('fields.phone-number')} onChange={this.onPhoneNumberChange}/>

				<div className={`complete-signup-button-container ${state.isSubmitting ? 'submitting' : ''}`}>
					<span className='signing-up-msg'>{state.isSubmitting ? compTranslate('signin-up') : null}</span>
					<button className='primary-btn' onClick={this.safelyExecuteCaptcha}>
						{state.isSubmitting ? <TinyLoader/> : compTranslate('get-started-cta')}
					</button>
				</div>
			</form>
		);

		return (
			<div className='complete-signup-container'>
				<header>
					<h1>{compTranslate('title-step2')}</h1>
					<h2>{compTranslate('complete-signup-desc')}</h2>
				</header>

				<main className='complete-signup-form-container'>{formComp}</main>

				<div id='signup-terms'><RenderHtml html={compTranslate('new-footer')}/></div>
				<ReCaptcha
					className='recaptcha-container'
					ref={this.captchaRef}
					size='invisible'
					onChange={this.onCaptchaResolve}
					sitekey={CAPTCHA_SITE_KEY}
					badge='bottomright'
				/>
			</div>
		);
	}
}
